<template>
  <article class="top-card calendar-card" :aria-label="$t('replay.playCalendar')">
    <div class="card-icon" aria-hidden="true">🗓️</div>
    <div class="card-label">{{ $t('replay.playCalendar') }}</div>
    <div class="cal-scroll">
      <div class="cal">
        <div class="cal-months" aria-hidden="true">
          <span class="cal-weekday-spacer"></span>
          <span
            v-for="(week, i) in weeks"
            :key="`m-${i}`"
            class="cal-month"
          >{{ week.monthLabel }}</span>
        </div>
        <div class="cal-body">
          <div class="cal-weekdays" aria-hidden="true">
            <span v-for="(label, i) in weekdayLabels" :key="i" class="cal-weekday">{{ label }}</span>
          </div>
          <div class="cal-weeks" @pointerleave="activeDay = null">
            <div v-for="(week, i) in weeks" :key="i" class="cal-week">
              <span
                v-for="(day, j) in week.days"
                :key="j"
                class="cal-cell"
                :class="[day ? `cal-cell--l${day.level}` : 'cal-cell--void', { 'cal-cell--active': day && day === activeDay }]"
                @pointerenter="day && (activeDay = day)"
                @click="day && (activeDay = day)"
              ></span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="cal-footer">
      <span class="cal-detail" role="status" aria-live="polite">{{ activeDay ? activeDay.title : '' }}</span>
      <span class="cal-legend" aria-hidden="true">
        <span class="cal-legend-label">{{ $t('replay.less') }}</span>
        <span v-for="l in 5" :key="l" class="cal-cell" :class="`cal-cell--l${l - 1}`"></span>
        <span class="cal-legend-label">{{ $t('replay.more') }}</span>
      </span>
    </div>
  </article>
</template>

<script>
const DAY_MS = 86400000

// UTC day key math — all dates in this app are UTC YYYY-MM-DD keys.
const keyToUtc = key => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key || '')
  if (!m) return null
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}
const utcToKey = d => d.toISOString().slice(0, 10)

export default {
  name: 'ActivityCalendar',
  data() {
    return {
      // Day under the pointer (or last tapped, on touch). Shown in the fixed
      // footer line instead of a floating/native tooltip — instant on hover
      // and it works on touch, where title attributes never show.
      activeDay: null
    }
  },
  props: {
    // Filter-scoped per-(date, app_id, minutes) records, same shape as the
    // other replay cards consume.
    playtimeData: { type: Array, default: () => [] },
    // Active filter bounds as ISO keys; the grid pads to full weeks around
    // them so the shape stays a GitHub-style rectangle.
    rangeStart: { type: String, default: '' },
    rangeEnd: { type: String, default: '' }
  },
  computed: {
    locale() {
      return this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US'
    },
    perDayMinutes() {
      const perDay = new Map()
      for (const r of this.playtimeData) {
        const date = typeof r.date === 'string' ? r.date.slice(0, 10) : ''
        if (!date) continue
        const minutes = Number(r.playtime_forever) || 0
        if (minutes <= 0) continue
        perDay.set(date, (perDay.get(date) || 0) + minutes)
      }
      return perDay
    },
    // Quantile thresholds over non-zero days, so the color ramp adapts to the
    // player's own volume instead of a fixed hour scale.
    thresholds() {
      const values = [...this.perDayMinutes.values()].sort((a, b) => a - b)
      if (!values.length) return [0, 0, 0]
      const q = p => values[Math.min(values.length - 1, Math.floor(p * values.length))]
      return [q(0.25), q(0.5), q(0.75)]
    },
    bounds() {
      let start = keyToUtc(this.rangeStart)
      let end = keyToUtc(this.rangeEnd)
      if (!start || !end) {
        const keys = [...this.perDayMinutes.keys()].sort()
        if (!keys.length) return null
        start = start || keyToUtc(keys[0])
        end = end || keyToUtc(keys[keys.length - 1])
      }
      if (!start || !end || start > end) return null
      return { start, end }
    },
    weeks() {
      if (!this.bounds) return []
      const { start, end } = this.bounds
      // Monday-start weeks (matches Steam's own grids).
      const lead = (start.getUTCDay() + 6) % 7
      const first = new Date(start.getTime() - lead * DAY_MS)
      const monthFmt = new Intl.DateTimeFormat(this.locale, { month: 'short', timeZone: 'UTC' })
      const dayFmt = new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
      const [q1, q2, q3] = this.thresholds

      const weeks = []
      let lastLabeledWeek = -10
      for (let w = 0; ; w++) {
        const weekStart = new Date(first.getTime() + w * 7 * DAY_MS)
        if (weekStart > end) break
        const days = []
        let monthLabel = ''
        for (let d = 0; d < 7; d++) {
          const date = new Date(weekStart.getTime() + d * DAY_MS)
          if (date < start || date > end) {
            days.push(null)
            continue
          }
          if (date.getUTCDate() === 1 && !monthLabel && w - lastLabeledWeek >= 2) {
            monthLabel = monthFmt.format(date)
            lastLabeledWeek = w
          }
          const minutes = this.perDayMinutes.get(utcToKey(date)) || 0
          let level = 0
          if (minutes > 0) {
            level = minutes <= q1 ? 1 : minutes <= q2 ? 2 : minutes <= q3 ? 3 : 4
          }
          days.push({
            level,
            title: `${dayFmt.format(date)}: ${this.formatPlaytime(minutes)}`
          })
        }
        // Label the very first month too when the range starts mid-month.
        if (w === 0 && !monthLabel) {
          monthLabel = monthFmt.format(start)
          lastLabeledWeek = 0
        }
        weeks.push({ days, monthLabel })
      }
      return weeks
    },
    weekdayLabels() {
      // Mon/Wed/Fri rows labeled, like GitHub — 2026-08-10 is a known Monday.
      const fmt = new Intl.DateTimeFormat(this.locale, { weekday: 'short', timeZone: 'UTC' })
      const monday = Date.UTC(2026, 7, 10)
      return [0, 1, 2, 3, 4, 5, 6].map(i =>
        i === 0 || i === 2 || i === 4 ? fmt.format(new Date(monday + i * DAY_MS)) : ''
      )
    }
  },
  methods: {
    formatPlaytime(minutes) {
      const m = Number(minutes) || 0
      if (m <= 0) return this.$t('common.zeroMinutes')
      if (m < 60) return this.$t('common.minutesOnly', { m })
      const hours = Math.floor(m / 60)
      const remainingMinutes = m % 60
      return remainingMinutes > 0
        ? this.$t('common.hoursMinutes', { h: hours, m: remainingMinutes })
        : this.$t('common.hoursOnly', { h: hours })
    }
  }
}
</script>

<style scoped>
/* Card chrome — matches SummaryMetricCard.vue exactly */
.top-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.2s ease;
}

.top-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), rgba(102, 192, 244, 0.25));
  border-radius: 8px 8px 0 0;
}

.top-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  border-color: rgba(42, 71, 94, 0.9);
}

.card-icon {
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: 8px;
  color: var(--primary);
}

.card-label {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

/* The grid itself. min-width: 0 on the card (set by the parent grid class)
   plus a scrollable wrap keeps wide ranges from stretching the page on
   phones — the year view simply scrolls sideways. */
.cal-scroll {
  overflow-x: auto;
  min-width: 0;
  padding-bottom: 4px;
  text-align: center;
}

.cal {
  display: inline-block;
  text-align: left;
}

.cal-months {
  display: flex;
  gap: 3px;
  margin-bottom: 4px;
}

.cal-weekday-spacer {
  /* Must equal .cal-weekdays' flex-basis (27px) — the shared 3px comes from
     each row's own gap. 30px here pushed every month label 3px right. */
  flex: 0 0 27px;
}

.cal-month {
  /* min-width: 0 is load-bearing: without it flexbox's min-width:auto lets
     the text widen the 12px box and every later label drifts right — the
     last month ended up a full column off. Text overflows the box instead. */
  flex: 0 0 12px;
  min-width: 0;
  font-size: 0.65rem;
  color: var(--muted);
  white-space: nowrap;
  overflow: visible;
  line-height: 1;
}

.cal-body {
  display: flex;
  gap: 3px;
}

.cal-weekdays {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 0 0 27px;
}

.cal-weekday {
  height: 12px;
  font-size: 0.65rem;
  color: var(--muted);
  line-height: 12px;
  text-align: right;
  padding-right: 2px;
}

.cal-weeks {
  display: flex;
  gap: 3px;
}

.cal-week {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cal-cell {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: rgba(102, 192, 244, 0.07);
  flex: 0 0 auto;
}

.cal-cell--void {
  background: transparent;
}

.cal-cell--l1 { background: #12496b; }
.cal-cell--l2 { background: #1a6898; }
.cal-cell--l3 { background: #2d8dc9; }
.cal-cell--l4 { background: #66c0f4; }

.cal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  min-height: 16px;
}

.cal-detail {
  font-size: 0.78rem;
  color: var(--text);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-cell--active {
  outline: 1px solid var(--primary);
  outline-offset: 1px;
}

.cal-legend {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.cal-legend-label {
  font-size: 0.7rem;
  color: var(--muted);
}
</style>
